// refer to https://redux.js.org/usage/deriving-data-selectors#optimizing-selectors-with-memoization
// can be updated with ws when ready
import { createSelector, createSelectorCreator } from '@reduxjs/toolkit';
// @ts-ignore
import memonize from 'lodash.memoize';

import { RootState } from '../store';
import { Address } from './gameSlice';
import { PLAYER_STATUS_ACTION } from '../constants/game';
import { getHandName } from '../utils/getHandName';

// @ts-ignore
const hashFn = (...args) => JSON.stringify(args);
const customSelectorCreator = createSelectorCreator(memonize, hashFn);

// reselect admin information
export const reselectGameAdminInfo = createSelector(
  [
    (state: RootState) => state.game.gameInfo?.adminAddress,
    (state: RootState) => state.game.gameInfo?.adminNickname,
  ],
  (adminAddress, adminNickname) => ({ adminAddress, adminNickname })
);

// for header
const selectGameBB = (state: RootState) =>
  state.game.gameInfo?.config?.bigBlind;
const selectGameSB = (state: RootState) =>
  state.game.gameInfo?.config?.smallBlind;
const selectGameStatus = (state: RootState) => state.game.gameInfo?.status;

export const reselectGameMoneyConfig = customSelectorCreator(
  [selectGameBB, selectGameSB, selectGameStatus],
  (bigBlind, smallBlind, gameStatus) => ({
    bigBlind,
    smallBlind,
    gameStatus,
  })
);

// for game setting page
export const reselectGameSetting = customSelectorCreator(
  [
    (state: RootState) => state.game.gameInfo?.config,
    (state: RootState) => state.game.gameInfo?.network,
    selectGameStatus,
  ],
  (config, network, gameStatus) =>
    ({
      ...config,
      network,
      gameStatus,
    } as Record<string, any>)
);

const selectUserInfo = (state: RootState) => state.game.userInfo?.users;
const selectAdditionalUserInfo = (state: RootState) =>
  state.game.addtionalUserInfos;
const selectOnSitUserInfo = (state: RootState) =>
  state.game.addtionalResp?.sitUsers;

// for payerlist page
export const reselectUserInfoInPlayerList = customSelectorCreator(
  [selectUserInfo, selectAdditionalUserInfo, selectOnSitUserInfo],
  (userInfo, additionalUserInfo, onSitUserInfo) => {
    const finalArr = [];
    if (userInfo) {
      for (const key of Object.keys(userInfo) as Address[]) {
        const userItem = {
          userAddress: userInfo[key].userAddress,
          balance: userInfo[key].balance,
          nickname: userInfo[key].nickname,
          isAway: additionalUserInfo?.[key]?.isAway ?? false,
          userStatus:
            additionalUserInfo?.[key]?.status ?? PLAYER_STATUS_ACTION.READY,
        };
        finalArr.push(userItem);
      }
    }
    if (onSitUserInfo) {
      for (const [key, value] of Object.entries(onSitUserInfo)) {
        const userItem = {
          userAddress: key as Address,
          balance: value.balance,
          nickname: value.nickname,
          isAway: value.isAway,
          userStatus: PLAYER_STATUS_ACTION.READY,
        };
        finalArr.push(userItem);
      }
    }
    return finalArr;
  }
);

// for topup
export const reselectUserBalances = customSelectorCreator(
  [selectUserInfo],
  userInfo => {
    if (!userInfo) return [];
    const users = Object.values(userInfo);
    return users.map(item => item.balance);
  }
);

// for game page
export const reselectSelfCardInfo = createSelector(
  [
    (state: RootState) => state.game.cardInfo?.holeCards,
    (state: RootState) => state.game.cardInfo?.communityCards,
    (state: RootState) => state.game.cardInfo?.rabbitHuntingCards,
    (state: RootState) => state.game.gameInfo?.config?.allowRabbitHunting,
    (state: RootState) => state.common.showRabbitHuntingCards,
  ],
  (
    holeCards,
    communityCards,
    rabbitHuntingCards,
    allowRabbitHunting,
    showRabbitHuntingCards
  ) => ({
    holeCards,
    handName: getHandName(
      holeCards,
      communityCards,
      allowRabbitHunting && showRabbitHuntingCards && rabbitHuntingCards
    ),
  })
);
export const reselectOtherCardInfo = createSelector(
  [(state: RootState) => state.game.cardInfo?.otherCards],
  otherCards => ({
    otherCards,
  })
);
export const reselectCommunityCards = createSelector(
  [(state: RootState) => state.game.cardInfo?.communityCards],
  communityCards => ({
    communityCards,
  })
);

// for hand log
export const reselectRoundId = createSelector(
  [(state: RootState) => state.game.gameInfo?.roundId],
  roundId => ({
    curRoundId: roundId,
  })
);

// for chat box
export const reselectPlayerNicknameObj = createSelector(
  [
    (state: RootState) => state.game.userInfo?.users,
    (state: RootState) => state.game.addtionalResp?.sitUsers,
  ],
  (users = {}, sitUsers = {}) => {
    const finalObj = {} as Record<string, string | undefined>;
    for (const key of Object.keys(users) as Address[]) {
      finalObj[key] = users[key].nickname;
    }
    for (const key of Object.keys(sitUsers) as Address[]) {
      finalObj[key] = sitUsers[key].nickname;
    }
    return finalObj;
  }
);

// for rabbit hunting
export const reselectRabbitHunting = createSelector(
  [
    (state: RootState) => state.game.cardInfo?.rabbitHuntingCards,
    (state: RootState) => state.game.gameInfo?.config?.allowRabbitHunting,
    (state: RootState) => state.common.showRabbitHuntingCards,
  ],
  (rabbitHuntingCards, allowRabbitHunting, showRabbitHuntingCards) => ({
    rabbitHuntingCards,
    allowRabbitHunting,
    showRabbitHuntingCards,
  })
);
